import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Download, FileText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

/** The exact filename Apple fetches. No extension — that is not a mistake. */
const FILE_NAME = 'apple-developer-merchantid-domain-association';

/**
 * View, copy and download the PPIID domain-verification file.
 *
 * Domains routed through our own storefront serving get this file automatically.
 * This exists for the other case: a merchant hosting checkout on infrastructure
 * we don't control, who has to place the file themselves. Without it that
 * handover means someone digging the blob out of the database.
 *
 * One file covers every domain under the platform integrator — unlike the old
 * Merchant-ID model, where Apple issued a distinct file per domain.
 */
/**
 * `trigger` lets a caller open this from its own element — the instructions
 * card links to it inline, so the file is reachable from the sentence that
 * tells you to send it rather than only from the panel above.
 */
export function AssociationFileDialog({ content, trigger }: { content: string; trigger?: ReactNode }) {
  const { t } = useTranslation('manageApplePay');
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const download = () => {
    // Blob, not a data: URI — the content is plain text and must land on disk
    // byte-for-byte, with no trailing newline added.
    const url = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = FILE_NAME;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm">
            <FileText className="mr-1.5 h-3.5 w-3.5" />
            {t('integrator.viewFile')}
          </Button>
        )}
      </DialogTrigger>

      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('integrator.fileTitle')}</DialogTitle>
          <DialogDescription>{t('integrator.fileDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('integrator.filePath')}
            </p>
            <code className="bg-muted block rounded-md p-2 text-xs break-all">
              https://&lt;domain&gt;/.well-known/{FILE_NAME}
            </code>
          </div>

          <div className="space-y-1.5">
            <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
              {t('integrator.fileContents')} · {content.length} chars
            </p>
            <pre className="bg-muted max-h-48 overflow-auto rounded-md p-3 text-xs break-all whitespace-pre-wrap">
              {content}
            </pre>
          </div>

          <p className="text-muted-foreground text-xs">{t('integrator.fileServingNote')}</p>

          <div className="flex gap-2">
            <Button size="sm" onClick={download}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              {t('integrator.fileDownload')}
            </Button>
            <Button size="sm" variant="outline" onClick={copy}>
              {copied ? (
                <Check className="mr-1.5 h-3.5 w-3.5" />
              ) : (
                <Copy className="mr-1.5 h-3.5 w-3.5" />
              )}
              {copied ? t('integrator.fileCopied') : t('integrator.fileCopy')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
